import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/api/axios";
import { useAuthStore } from "@/store/auth";
import { useToastHandler } from "@/hooks/useToastHandler";
import { SetModelJsonResource } from "@/types/setTypes";

const ERROR_MSG = "Failed to fetch recent sets";
const API_PATH = "/api/sets/recent";
const USE_QUERY_KEY = API_PATH;

// Hook for fetching recent sets using useQuery, structured like useUserInfo
export function useRecentSets() {
  const { token } = useAuthStore();
  const { handleError } = useToastHandler();

  return useQuery<SetModelJsonResource[] | null>({
    enabled: !!token,
    queryKey: [USE_QUERY_KEY],
    //refetchInterval: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      try {
        const res = await api.get(API_PATH);
        if (import.meta.env.DEV) {
          console.log(
            `>>> React Hook Fetching .... [${USE_QUERY_KEY}]`,
            res.data
          );
        }

        if (!res.data || !Array.isArray(res.data)) {
          throw new Error(ERROR_MSG + "(Invalid Data)");
        }
        return res.data;
      } catch (err: unknown) {
        handleError(
          err instanceof Error ? err.message : ERROR_MSG,
          3000,
          err instanceof Error ? err.stack ?? "" : ""
        );
        //You should throw the error in your query function, not `return null`.
        // This way, React Query will set isError: true and your error UI will be shown.
        throw err;
      }
    },
  });
}

// invalidate cache:
export function useInvalidateRecentSets() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [USE_QUERY_KEY] });
  };
}

// refetch cache:
export function useRefetchRecentSets() {
  const { refetch } = useRecentSets();
  return refetch;
}
